import React, { useState, useEffect } from 'react';
import { BookOpen, Plus, Trash2, Download, X, Send } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : '/api');

export default function CoachWorkoutLibrary({ onClose, clients = [] }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ title: '', type: 'Run', description: '', durationMinutes: 60 });
  const [assigningTemplate, setAssigningTemplate] = useState(null);
  const [assignData, setAssignData] = useState({ clientId: '', date: '' });
  const [assignLoading, setAssignLoading] = useState(false);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/coach/workout-templates`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data);
      }
    } catch (e) {
      console.error('Error fetching templates:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleAddTemplate = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/coach/workout-templates`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(newTemplate)
      });
      if (res.ok) {
        const added = await res.json();
        setTemplates([...templates, added]);
        setShowAdd(false);
        setNewTemplate({ title: '', type: 'Run', description: '', durationMinutes: 60 });
      }
    } catch (e) {
      console.error('Error adding template:', e);
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${API_URL}/coach/workout-templates/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        setTemplates(templates.filter(t => t.id !== id));
      }
    } catch (e) {
      console.error('Error deleting template:', e);
    }
  };

  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    if (!assignData.clientId || !assignData.date || !assigningTemplate) return;
    setAssignLoading(true);
    try {
      const res = await fetch(`${API_URL}/workouts/planned?userId=${assignData.clientId}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          date: assignData.date,
          type: assigningTemplate.type,
          title: assigningTemplate.title,
          description: assigningTemplate.description,
          durationMinutes: assigningTemplate.durationMinutes
        })
      });
      if (res.ok) {
        alert('Entrenamiento asignado correctamente al calendario del atleta.');
        setAssigningTemplate(null);
        setAssignData({ clientId: '', date: '' });
      } else {
        const err = await res.json();
        alert('Error: ' + (err.error || 'No se pudo asignar el entrenamiento.'));
      }
    } catch (e) {
      console.error('Error assigning template:', e);
      alert('Error de conexión.');
    } finally {
      setAssignLoading(false);
    }
  };

  const generatePDF = (template) => {
    // Implementación básica usando print de navegador
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Plan de Entrenamiento: ${template.title}</title>
          <style>
            body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; padding: 40px; color: #333; line-height: 1.6; }
            h1 { color: #ff5722; border-bottom: 2px solid #ff5722; padding-bottom: 10px; }
            .meta { font-size: 14px; color: #666; margin-bottom: 30px; }
            .description { background: #f9f9f9; padding: 20px; border-radius: 8px; border-left: 4px solid #10b981; white-space: pre-wrap; }
            .footer { margin-top: 50px; font-size: 12px; color: #999; text-align: center; }
          </style>
        </head>
        <body>
          <h1>${template.title}</h1>
          <div class="meta">
            <strong>Deporte:</strong> ${template.type} <br/>
            <strong>Duración estimada:</strong> ${template.durationMinutes} minutos
          </div>
          <div class="description">${template.description}</div>
          <div class="footer">Generado desde Plataforma Deportiva - Coach Panel</div>
          <script>
            window.onload = () => { window.print(); window.close(); }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="dashboard-main" style={{ animation: 'fadeInTab 0.3s ease-out' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '2rem', margin: 0 }}>
          <BookOpen size={32} color="var(--strava-orange)" /> Librería de Entrenamientos
        </h2>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <button 
            onClick={() => setShowAdd(!showAdd)}
            style={{ padding: '0.75rem 1.5rem', background: 'var(--strava-orange)', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            {showAdd ? 'Cancelar' : <><Plus size={18} /> Nueva Plantilla</>}
          </button>
          <button 
            onClick={onClose}
            style={{ padding: '0.75rem 1.5rem', background: 'rgba(255,255,255,0.1)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <X size={18} /> Cerrar Librería
          </button>
        </div>
      </div>

      {showAdd && (
        <div className="glass-panel" style={{ marginBottom: '2rem', padding: '1.5rem' }}>
          <h3 style={{ margin: '0 0 1.5rem 0' }}>Crear nueva plantilla</h3>
          <form onSubmit={handleAddTemplate} style={{ display: 'grid', gap: '1.5rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
              <div>
                <label className="text-muted text-sm">Título del Entrenamiento</label>
                <input type="text" value={newTemplate.title} onChange={e => setNewTemplate({...newTemplate, title: e.target.value})} required style={{ width: '100%', padding: '0.8rem', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', color: 'white', marginTop: '0.5rem' }} />
              </div>
              <div>
                <label className="text-muted text-sm">Deporte</label>
                <select value={newTemplate.type} onChange={e => setNewTemplate({...newTemplate, type: e.target.value})} style={{ width: '100%', padding: '0.8rem', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', color: 'white', marginTop: '0.5rem' }}>
                  <option value="Run" style={{ color: 'black' }}>Carrera</option>
                  <option value="Ride" style={{ color: 'black' }}>Ciclismo</option>
                  <option value="Swim" style={{ color: 'black' }}>Natación</option>
                  <option value="Workout" style={{ color: 'black' }}>Fuerza / Gym</option>
                </select>
              </div>
              <div>
                <label className="text-muted text-sm">Duración (minutos)</label>
                <input type="number" value={newTemplate.durationMinutes} onChange={e => setNewTemplate({...newTemplate, durationMinutes: parseInt(e.target.value)})} style={{ width: '100%', padding: '0.8rem', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', color: 'white', marginTop: '0.5rem' }} />
              </div>
            </div>
            <div>
              <label className="text-muted text-sm">Descripción del bloque (Calentamiento, Series, Enfriamiento...)</label>
              <textarea value={newTemplate.description} onChange={e => setNewTemplate({...newTemplate, description: e.target.value})} required rows={5} style={{ width: '100%', padding: '0.8rem', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', color: 'white', marginTop: '0.5rem', resize: 'vertical' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="submit" style={{ padding: '0.75rem 2rem', background: 'var(--health-green)', color: 'black', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>
                Guardar Plantilla
              </button>
            </div>
          </form>
        </div>
      )}

      {assigningTemplate && (
        <div className="glass-panel" style={{ marginBottom: '2rem', padding: '1.5rem', border: '2px solid var(--health-green)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Send size={20} color="var(--health-green)" /> Asignar "{assigningTemplate.title}"
            </h3>
            <button onClick={() => setAssigningTemplate(null)} style={{ background: 'transparent', border: 'none', color: 'white', cursor: 'pointer' }}><X size={20} /></button>
          </div>
          <form onSubmit={handleAssignSubmit} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem', alignItems: 'end' }}>
            <div>
              <label className="text-muted text-sm">Selecciona Atleta</label>
              <select value={assignData.clientId} onChange={e => setAssignData({...assignData, clientId: e.target.value})} required style={{ width: '100%', padding: '0.8rem', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', color: 'white', marginTop: '0.5rem' }}>
                <option value="" disabled style={{ color: 'black' }}>Elige un atleta...</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id} style={{ color: 'black' }}>{c.name} ({c.email})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-muted text-sm">Fecha de Entrenamiento</label>
              <input type="date" value={assignData.date} onChange={e => setAssignData({...assignData, date: e.target.value})} required style={{ width: '100%', padding: '0.8rem', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', color: 'white', marginTop: '0.5rem' }} />
            </div>
            <button type="submit" disabled={assignLoading} style={{ padding: '0.8rem 2rem', background: 'var(--health-green)', color: 'black', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', height: 'fit-content' }}>
              {assignLoading ? 'Asignando...' : 'Confirmar Asignación'}
            </button>
          </form>
        </div>
      )}

      {loading ? (
        <div style={{ padding: '3rem', textAlign: 'center' }}>Cargando librería...</div>
      ) : templates.length === 0 ? (
        <div className="glass-panel" style={{ padding: '4rem', textAlign: 'center' }}>
          <BookOpen size={48} color="rgba(255,255,255,0.2)" style={{ marginBottom: '1rem' }} />
          <h3>Librería vacía</h3>
          <p className="text-muted">Aún no has creado ninguna plantilla de entrenamiento.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
          {templates.map(t => (
            <div key={t.id} className="glass-panel" style={{ display: 'flex', flexDirection: 'column', padding: '1.5rem', position: 'relative' }}>
              <button onClick={() => handleDelete(t.id)} style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', opacity: 0.7 }} onMouseOver={e => e.target.style.opacity = 1} onMouseOut={e => e.target.style.opacity = 0.7}>
                <Trash2 size={18} />
              </button>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', color: 'var(--health-cyan)', fontSize: '0.85rem', fontWeight: 'bold' }}>
                {t.type} • {t.durationMinutes} min
              </div>
              <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.25rem' }}>{t.title}</h3>
              
              <div style={{ flex: 1, background: 'rgba(0,0,0,0.2)', padding: '1rem', borderRadius: '8px', fontSize: '0.9rem', color: 'var(--text-muted)', marginBottom: '1.5rem', maxHeight: '100px', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                {t.description}
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                <button 
                  onClick={() => {
                    setAssigningTemplate(t);
                    setShowAdd(false);
                  }}
                  style={{ width: '100%', padding: '0.75rem', background: 'var(--health-green)', color: 'black', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', fontWeight: 'bold' }}
                >
                  <Send size={16} /> Asignar
                </button>
                <button 
                  onClick={() => generatePDF(t)}
                  style={{ width: '100%', padding: '0.75rem', background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', transition: 'all 0.2s' }}
                  onMouseOver={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'; }}
                  onMouseOut={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
                >
                  <Download size={16} /> PDF
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
